import _ from 'lodash';
import { getAttributeKeys } from '../index';
import { isUnlimited } from '../../../packagesUtils';
import { MULTIPLE_VALUE, SINGLE_VALUE } from '../../../pricingFactors/constants';
import { Attributes } from '../../attributes';

/**
 *  Get label of a single pricing factor. In case we have a specific configuration
 *  for the gig sub category, we should get that one, otherwise it will fallback to
 *  the basic label.
 *
 *  For example, for the following configuration and the sub category [353]:
 *
 *  "word_count": {
 *    "label": "Words Included",
 *    "sub_category_353": {
 *      "label": "Pages Included"
 *    }
 *  }
 *
 *  We should get "Pages Included".
 */
export const getLabel = ({ i18n, name, subCategoryId, options = {} }) => {
    const attributes = [Attributes.label];
    const attributeKeys = getAttributeKeys({ name, subCategoryId, attributes, options });

    return i18n.t(attributeKeys);
};

/**
 *  Get calculator formatted label of a single calculator.
 */
export const getCalculatorFormattedLabel = ({ i18n, name, value, subCategoryId }) =>
    getFormattedLabel({
        i18n,
        name,
        value,
        subCategoryId,
        options: {
            withCalculator: true
        }
    });

/**
 *  Get formatted label of a single pricing factor. The formatted label is designed to handle
 *  pricing factors of type "Number" (Pricing factors representing a single numeric value).
 *
 *  For example, for the following configuration, sub category [353] and the values 1, 5, -1:
 *
 *  "word_count": {
 *    "buyer_title": {
 *      "one": "Up to %{count} Word",
 *      "other": "Up to %{count} Words"
 *    }
 *    "sub_category_353": {
 *      "buyer_title": {
 *        "one": "Just one page"
 *      }
 *    }
 *  }
 *
 *  We should get "Just one page", "Up to 5 Words", "Up to Unlimited Words" respectively.
 */
export const getFormattedLabel = ({ i18n, name, value, subCategoryId, options = {} }) => {
    const attributes = buildFormattedLabelAttributes(value);
    const attributeKeys = getAttributeKeys({ name, subCategoryId, attributes, options });
    const formattedLabel = i18n.t(attributeKeys, { count: getPresentedCount(i18n, value) });

    return _.isString(formattedLabel) ? formattedLabel : name;
};

const buildFormattedLabelAttributes = (value) => {
    const multipleValueSuffix = isSingleValue(value) ? SINGLE_VALUE : MULTIPLE_VALUE;
    const multipleValueAttribute = `${Attributes.buyerTitle}.${multipleValueSuffix}`;

    return [Attributes.title, multipleValueAttribute, Attributes.label];
};

const isSingleValue = (value) => Number(value) === 1;

const getPresentedCount = (i18n, value) => isUnlimited(value)
    ? i18n.t('gig_page_perseus.packages.unlimited')
    : value;
